import { useState, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import TabList from '@mui/lab/TabList'
import Tab from '@mui/material/Tab'
import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ListIcon from '@mui/icons-material/List'
import AddBoxIcon from '@mui/icons-material/AddBox'
import DashboardIcon from '@mui/icons-material/Dashboard'
import {
  useNavigate,
  useBlocker,
  useParams,
  useSearchParams,
  useSubmit,
  Outlet,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'
import cloneDeep from 'lodash/cloneDeep'

import { useAppDispatch, useAppSelector } from '../../../../store'
import { setSessions, getEventDetails, setSubEvents } from '../../../../store.slice'
import { createUniqueDeepClone } from '../../../../utils'
import { ModifySubEventsSchema } from '../../../events/Events.schema'
import Form from '../../../root/components/Form'
import { useContextStep } from '../CreateEvents.Context'
import { defaultSession, defaultSubEvent } from './components/SubEvents.utils'
import { onSubmitData } from '../CreateEvent.utils'
import Step2CalendarView from './Step2CalendarView'
import Step2ViewList from './Step2ViewList'
import Step2DashboardView from './Step2DashboardView'
import { SubEventsStep2Context } from './SubEventsStep2.Context'

export default function SubEventsStep2() {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const submit = useSubmit()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const viewType = searchParams.get('viewType') ?? 'dashboard'
  const [customScrollParent, setCustomScrollParent] = useState<HTMLFormElement | null>(null)
  const { eventId } = useParams()
  const [step, setStep] = useContextStep()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const [tab, setTab] = useState(viewType)
  useEffect(() => {
    if (viewType !== tab) {
      setTab(viewType)
    }
  }, [viewType, tab])

  const eventDetails = useAppSelector(state => getEventDetails(state)(eventId))

  const methods = useForm<ModifySubEventsSchema>({
    defaultValues: {
      sessions: cloneDeep(eventDetails.sessions),
      subEvents: cloneDeep(eventDetails.subEvents),
    },
    resolver: zodResolver(ModifySubEventsSchema),
  })
  const { watch, getValues, control } = methods

  const {
    fields: _sessionsFields,
    append: appendSession,
    remove: removeSession,
    update: updateSession,
  } = useFieldArray({
    control,
    name: `sessions`,
  })

  const {
    fields: _subEventsFields,
    append: appendSubEvent,
    remove: removeSubEvent,
    update: updateSubEvent,
  } = useFieldArray({
    control,
    name: `subEvents`,
  })

  useBlocker(({ nextLocation }) => {
    const values = getValues()
    dispatch(setSessions({ data: values.sessions, key: eventId }))
    dispatch(setSubEvents({ data: values.subEvents, key: eventId }))

    if (!nextLocation.pathname.includes('/admin/events/')) {
      return !window.confirm(
        `${t(
          'Êtes-vous sûr de vouloir quitter cette page ? Des modifications non enregistrées seront perdues'
        )}.`
      )
    }
    return false
  })

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const values = getValues()
    const data = {
      ...eventDetails,
      sessions: values.sessions,
      subEvents: values.subEvents,
    }
    dispatch(setSessions({ data: values.sessions, key: eventId }))
    dispatch(setSubEvents({ data: values.subEvents, key: eventId }))
    onSubmitData(data, eventId, submit)
  }

  const _watchedSubEvents = watch('subEvents')
  const _watchedSessions = watch('sessions')
  const subEventsFields = _subEventsFields.map((field, index) => ({
    ...field,
    ..._watchedSubEvents[index],
  }))
  const sessionsFields = _sessionsFields.map((field, index) => ({
    ...field,
    ..._watchedSessions[index],
  }))

  return (
    <SubEventsStep2Context.Provider
      value={{
        subEventsFields,
        appendSubEvent,
        removeSubEvent,
        updateSubEvent,
        sessionsFields,
        appendSession,
        removeSession,
        updateSession,
        eventStart: eventDetails?.event?.start,
        eventEnd: eventDetails?.event?.end,
        eventLocation: eventDetails?.event?.location,
      }}
    >
      <FormProvider {...methods}>
        <Form ref={setCustomScrollParent} onSubmit={onSubmitForm}>
          <TabContext value={tab}>
            <Stack direction="column" spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <h1>{t('Séances et interventions')}</h1>
                <Stack
                  direction="row"
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{
                    position: 'fixed',
                    zIndex: '10',
                    right: '3rem',
                  }}
                >
                  <Button
                    startIcon={<AddBoxIcon />}
                    variant="contained"
                    onClick={event => {
                      event.preventDefault()
                      const newSession = createUniqueDeepClone(
                        defaultSession(eventId === 'new' ? -1 : Number(eventId))
                      )
                      appendSession(newSession)
                      navigate(
                        `/admin/events/${eventId}/sessions/${newSession.id}?viewType=${viewType}`
                      )
                    }}
                    sx={{ pointerEvents: 'all' }}
                  >
                    {t('Séance')}
                  </Button>
                  <Button
                    startIcon={<AddBoxIcon />}
                    variant="contained"
                    onClick={event => {
                      event.preventDefault()
                      const newSubEvent = createUniqueDeepClone(
                        defaultSubEvent(eventId === 'new' ? -1 : Number(eventId))
                      )
                      appendSubEvent(newSubEvent)
                      navigate(
                        `/admin/events/${eventId}/sessions/subEvent/${newSubEvent.id}?viewType=${viewType}`
                      )
                    }}
                    sx={{ pointerEvents: 'all' }}
                  >
                    {t('Intervention')}
                  </Button>

                  <TabList
                    onChange={(_event, newValue: string) => {
                      setTab(newValue)
                      setSearchParams({ viewType: newValue })
                    }}
                    sx={theme => ({
                      backgroundColor: theme.colors.whiteBackground,
                      borderRadius: '1rem 1rem 0 0',
                    })}
                  >
                    <Tab icon={<DashboardIcon />} aria-label="tableau" value="dashboard" />
                    <Tab icon={<ListIcon />} aria-label="liste" value="list" />
                    <Tab icon={<CalendarMonthIcon />} aria-label="calendrier" value="calendar" />
                  </TabList>
                </Stack>
              </Stack>
              <Divider />

              <TabPanel value="dashboard">
                <Step2DashboardView />
              </TabPanel>

              <TabPanel value="list">
                <Step2ViewList customScrollParent={customScrollParent} />
              </TabPanel>

              <TabPanel value="calendar">
                <Step2CalendarView />
              </TabPanel>

              <Stack sx={{ minHeight: '3.5rem' }} />
              <Stack
                direction="row"
                padding="0 2rem 0.5rem"
                sx={{
                  position: 'fixed',
                  zIndex: 1,
                  width: '100%',
                  bottom: { xs: '7rem', md: '3rem', lg: '2.5rem' },
                  left: 0,
                  pointerEvents: 'none',
                }}
              >
                <Stack minWidth={{ xs: '0', md: '340px', lg: '400px' }} />
                <Stack
                  direction="row"
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  justifyContent="space-between"
                  padding="0 1rem"
                  flex="2 0 auto"
                >
                  <Button
                    onClick={event => {
                      event.preventDefault()
                      if (window.confirm(t('Êtes-vous sûr de vouloir annuler ?')))
                        navigate('/dashboard')
                    }}
                    sx={{ pointerEvents: 'all' }}
                  >
                    {t('Annuler')}
                  </Button>
                  <Stack direction="row" spacing={{ xs: 1, sm: 2, md: 3 }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        navigate(`/admin/events/${eventId}/event`)
                      }}
                      sx={{ pointerEvents: 'all' }}
                    >
                      {t('Précédent')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        methods
                          .trigger()
                          .then(valid => {
                            if (!valid) {
                              toast.error(t('Veuillez corriger les erreurs'))
                              return
                            }
                            if (step < 3) {
                              setStep(3)
                            }
                            navigate(`/admin/events/${eventId}/logistic`)
                          })
                          .catch(() => {
                            toast.error(t('Une erreur est survenue'))
                          })
                      }}
                      sx={{ pointerEvents: 'all' }}
                    >
                      {t('Suivant')}
                    </Button>
                    <Button
                      disabled={step < 5}
                      variant="contained"
                      type="submit"
                      sx={{ pointerEvents: 'all' }}
                    >
                      {t('Valider')}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <Outlet />
          </TabContext>
        </Form>
      </FormProvider>
    </SubEventsStep2Context.Provider>
  )
}
